import { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    Checkbox,
    ListItemText
} from '@mui/material'
import {
    Add,
    Edit,
    Delete,
    VpnKey,
    Person,
    Email as EmailIcon
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import axios from '@api/axios'
import { toast } from 'react-toastify'

const SystemUsers = () => {
    const { t, language } = useTranslation()
    const { user: currentUser } = useAuthStore()

    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [saving, setSaving] = useState(false)

    const [formData, setFormData] = useState({
        name_en: '',
        name_ar: '',
        email: '',
        password: '',
        user_type: 'admin',
        assigned_forms: []
    })

    const [newPassword, setNewPassword] = useState('')
    const [forms, setForms] = useState([]) // Available forms for assignment

    // Load users
    useEffect(() => {
        loadUsers()
    }, [])

    const loadUsers = async () => {
        try {
            setLoading(true)
            const [usersRes, formsRes] = await Promise.all([
                axios.get('/users'),
                axios.get('/forms')
            ])
            console.log('Users API response:', usersRes.data)
            // Handle both response structures
            const users = usersRes.data?.data || usersRes.data || []
            const formsData = formsRes.data?.data || formsRes.data || []
            console.log('Parsed users:', users)
            setUsers(Array.isArray(users) ? users : [])
            setForms(Array.isArray(formsData) ? formsData : [])
        } catch (err) {
            console.error('Failed to load users:', err)
            toast.error(language === 'ar' ? 'فشل تحميل المستخدمين' : 'Failed to load users')
        } finally {
            setLoading(false)
        }
    }

    const handleOpenDialog = (user = null) => {
        if (user) {
            setSelectedUser(user)
            setFormData({
                name_en: user.name_en || '',
                name_ar: user.name_ar || '',
                email: user.email || '',
                password: '',
                user_type: user.user_type || 'admin',
                assigned_forms: user.assigned_forms?.map(f => f.id) || []
            })
        } else {
            setSelectedUser(null)
            setFormData({
                name_en: '',
                name_ar: '',
                email: '',
                password: '',
                user_type: 'admin',
                assigned_forms: []
            })
        }
        setDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setSelectedUser(null)
    }

    const handleSubmit = async () => {
        try {
            setSaving(true)
            if (selectedUser) {
                // Update user
                const updateData = {
                    name_en: formData.name_en,
                    name_ar: formData.name_ar,
                    email: formData.email,
                    assigned_forms: formData.user_type === 'form_manager' ? formData.assigned_forms : []
                }
                await axios.put(`/users/${selectedUser.id}`, updateData)
                toast.success(language === 'ar' ? 'تم تحديث المستخدم' : 'User updated successfully')
            } else {
                // Create user - include user_type and assigned_forms
                const createData = {
                    ...formData,
                    assigned_forms: formData.user_type === 'form_manager' ? formData.assigned_forms : []
                }
                await axios.post('/users', createData)
                toast.success(language === 'ar' ? 'تم إنشاء المستخدم' : 'User created successfully')
            }
            handleCloseDialog()
            loadUsers()
        } catch (err) {
            console.error('Failed to save user:', err)
            toast.error(err.response?.data?.message || (language === 'ar' ? 'فشل الحفظ' : 'Failed to save'))
        } finally {
            setSaving(false)
        }
    }

    const handleOpenResetPassword = (user) => {
        setSelectedUser(user)
        setNewPassword('')
        setResetPasswordOpen(true)
    }

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters')
            return
        }
        try {
            setSaving(true)
            await axios.put(`/users/${selectedUser.id}/password`, { password: newPassword })
            toast.success(language === 'ar' ? 'تم إعادة تعيين كلمة المرور' : 'Password reset successfully')
            setResetPasswordOpen(false)
        } catch (err) {
            console.error('Failed to reset password:', err)
            toast.error(err.response?.data?.message || (language === 'ar' ? 'فشل إعادة تعيين كلمة المرور' : 'Failed to reset password'))
        } finally {
            setSaving(false)
        }
    }

    const handleDeleteUser = async (user) => {
        if (user.id === currentUser?.id) {
            toast.error(language === 'ar' ? 'لا يمكنك حذف نفسك' : 'You cannot delete yourself')
            return
        }
        if (!confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المستخدم؟' : 'Are you sure you want to delete this user?')) {
            return
        }
        try {
            await axios.delete(`/users/${user.id}`)
            toast.success(language === 'ar' ? 'تم حذف المستخدم' : 'User deleted successfully')
            loadUsers()
        } catch (err) {
            console.error('Failed to delete user:', err)
            toast.error(err.response?.data?.message || (language === 'ar' ? 'فشل الحذف' : 'Failed to delete'))
        }
    }

    const getUserTypeLabel = (type) => {
        const labels = {
            admin: language === 'ar' ? 'مدير النظام' : 'Admin',
            form_manager: language === 'ar' ? 'مدير نماذج' : 'Form Manager',
            scanner: language === 'ar' ? 'ماسح QR' : 'QR Scanner',
            dashboard: language === 'ar' ? 'لوحة التحكم' : 'Dashboard',
            department: language === 'ar' ? 'قسم' : 'Department'
        }
        return labels[type] || type
    }

    const getUserTypeColor = (type) => {
        switch (type) {
            case 'admin': return 'error'
            case 'form_manager': return 'warning'
            case 'scanner': return 'info'
            case 'dashboard': return 'primary'
            case 'department': return 'success'
            default: return 'default'
        }
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                    {language === 'ar' ? 'مديرو النظام' : 'System Administrators'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                >
                    {language === 'ar' ? 'إضافة مستخدم' : 'Add User'}
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : users.length === 0 ? (
                <Alert severity="info">
                    {language === 'ar' ? 'لا يوجد مستخدمين' : 'No users found'}
                </Alert>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{language === 'ar' ? 'الاسم' : 'Name'}</TableCell>
                                <TableCell>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</TableCell>
                                <TableCell>{language === 'ar' ? 'النوع' : 'Type'}</TableCell>
                                <TableCell>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                                <TableCell align="right">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person color="action" />
                                            {language === 'ar' && user.name_ar ? user.name_ar : user.name_en}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{user.email || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getUserTypeLabel(user.user_type)}
                                            color={getUserTypeColor(user.user_type)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.is_active ? (language === 'ar' ? 'نشط' : 'Active') : (language === 'ar' ? 'غير نشط' : 'Inactive')}
                                            color={user.is_active ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={language === 'ar' ? 'تعديل' : 'Edit'}>
                                            <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}>
                                            <IconButton size="small" color="warning" onClick={() => handleOpenResetPassword(user)}>
                                                <VpnKey />
                                            </IconButton>
                                        </Tooltip>
                                        {user.id !== currentUser?.id && (
                                            <Tooltip title={language === 'ar' ? 'حذف' : 'Delete'}>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit User Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedUser
                        ? (language === 'ar' ? 'تعديل المستخدم' : 'Edit User')
                        : (language === 'ar' ? 'إضافة مستخدم جديد' : 'Add New User')
                    }
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {/* User Type Selector (only for new users) */}
                        {!selectedUser && (
                            <FormControl fullWidth>
                                <InputLabel>{language === 'ar' ? 'نوع المستخدم' : 'User Type'}</InputLabel>
                                <Select
                                    value={formData.user_type}
                                    onChange={(e) => setFormData({ ...formData, user_type: e.target.value, assigned_forms: [] })}
                                    label={language === 'ar' ? 'نوع المستخدم' : 'User Type'}
                                >
                                    <MenuItem value="admin">{language === 'ar' ? 'مدير النظام (صلاحيات كاملة)' : 'Admin (Full Access)'}</MenuItem>
                                    <MenuItem value="form_manager">{language === 'ar' ? 'مدير نماذج (صلاحيات محدودة)' : 'Form Manager (Limited Access)'}</MenuItem>
                                    <MenuItem value="scanner">{language === 'ar' ? 'ماسح QR (ماسح فقط)' : 'QR Scanner (Scanner Only)'}</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <TextField
                            fullWidth
                            label={language === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)'}
                            value={formData.name_en}
                            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                            required
                        />
                        <TextField
                            fullWidth
                            label={language === 'ar' ? 'الاسم (عربي)' : 'Name (Arabic)'}
                            value={formData.name_ar}
                            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            InputProps={{
                                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                        />
                        {!selectedUser && (
                            <TextField
                                fullWidth
                                label={language === 'ar' ? 'كلمة المرور' : 'Password'}
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required
                                helperText={language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters'}
                            />
                        )}

                        {/* Form Assignment (only for form_manager) */}
                        {formData.user_type === 'form_manager' && (
                            <FormControl fullWidth>
                                <InputLabel>{language === 'ar' ? 'النماذج المعينة' : 'Assigned Forms'}</InputLabel>
                                <Select
                                    multiple
                                    value={formData.assigned_forms}
                                    onChange={(e) => setFormData({ ...formData, assigned_forms: e.target.value })}
                                    input={<OutlinedInput label={language === 'ar' ? 'النماذج المعينة' : 'Assigned Forms'} />}
                                    renderValue={(selected) => {
                                        const selectedForms = forms.filter(f => selected.includes(f.id))
                                        return selectedForms.map(f => f.title).join(', ')
                                    }}
                                >
                                    {forms.map((form) => (
                                        <MenuItem key={form.id} value={form.id}>
                                            <Checkbox checked={formData.assigned_forms.includes(form.id)} />
                                            <ListItemText primary={form.title} />
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {language === 'ar'
                                        ? 'مدير النماذج يمكنه فقط الوصول للنماذج المحددة'
                                        : 'Form Manager can only access selected forms'
                                    }
                                </Typography>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving || !formData.name_en || (!selectedUser && !formData.password)}
                    >
                        {saving ? <CircularProgress size={20} /> : (language === 'ar' ? 'حفظ' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? `إعادة تعيين كلمة المرور لـ ${selectedUser?.name_en}`
                            : `Reset password for ${selectedUser?.name_en}`
                        }
                    </Typography>
                    <TextField
                        fullWidth
                        label={language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        helperText={language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetPasswordOpen(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleResetPassword}
                        disabled={saving || !newPassword}
                    >
                        {saving ? <CircularProgress size={20} /> : (language === 'ar' ? 'إعادة تعيين' : 'Reset')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default SystemUsers
